import type { EditGraph, ResolvedEdit } from "../contracts/edit-graph.js";
import type { ScoreBreakdown } from "../contracts/score.js";
import type { Claim, SourcePack } from "../contracts/source-pack.js";
import { captionPresentationScoreBoost } from "../edit/caption-presentation.js";
import { creativePresentationScoreBoost } from "../edit/creative-presentation.js";
import { resolveEdit } from "../edit/reducer.js";
import { effectiveSound, isAudibleSound } from "../edit/sound.js";
import { addMilli, clampMilli, createSeededRng, mulMilli, type Milli } from "./fixed.js";

/**
 * Deterministic pure scoring over SourcePack + EditGraph.
 *
 * This is a **game model**, not moral truth or a fact-check verdict.
 * Identical (seed, pack, active ops) must always yield identical scores,
 * independent of renderer FPS or device speed.
 *
 * Context-loss formula (capped at 1000):
 *   sum(tag.contextLossMilli for tags on omitted contextCritical beats)
 *   + frame-exclusion penalties
 *
 * Trend boost (integer):
 *   presentMatchingTags / totalMatchingTags * trend.reachBoostMilli
 * so edits that drop matching material receive less boost.
 *
 * credDelta is the delayed publish consequence (contextLoss, claimDrift, and
 * Casting provenanceRisk). Computing it here does not imply a live moral meter;
 * the shell reveals Cred/provenance only at publish / delayed comparison.
 */
export function scoreEdit(
  pack: SourcePack,
  graph: EditGraph,
  resolved: ResolvedEdit = resolveEdit(graph, pack),
): ScoreBreakdown {
  const rng = createSeededRng(graph.seed);
  const beatById = new Map(pack.beats.map((b) => [b.id, b]));
  const entityById = new Map(pack.entities.map((e) => [e.id, e]));
  const tokenById = new Map(pack.captionVocabulary.tokens.map((t) => [t.id, t]));
  const assetById = new Map(pack.assets.map((a) => [a.id, a]));
  const tagById = new Map(pack.scoringTags.map((t) => [t.id, t]));

  const retainedBeats = resolved.beatOrder.filter((id) => {
    const cut = resolved.cuts.get(id);
    return cut?.mode !== "omit";
  });
  const retainedSet = new Set(retainedBeats);
  const omittedCritical = pack.beats.filter(
    (b) => b.contextCritical && !retainedSet.has(b.id),
  );

  // --- Context loss from declared tag weights on omitted critical beats ---
  let contextLoss: Milli = 0;
  for (const beat of omittedCritical) {
    for (const tagId of beat.scoringTagIds) {
      const tag = tagById.get(tagId);
      if (tag) contextLoss = addMilli(contextLoss, tag.contextLossMilli);
    }
  }

  for (const beatId of retainedBeats) {
    const frame = resolved.frames.get(beatId);
    const beat = beatById.get(beatId);
    if (!frame || !beat) continue;

    const tightCrop =
      frame.crop.wMilli <= 450 || frame.crop.hMilli <= 450 || frame.zoomMilli >= 1600;
    if (!tightCrop) continue;

    for (const entityId of beat.entityIds) {
      const entity = entityById.get(entityId);
      if (!entity?.contextCarrier) continue;
      if (frame.subjectEntityId && frame.subjectEntityId !== entityId) {
        contextLoss = addMilli(contextLoss, 120);
      } else if (!frame.subjectEntityId) {
        contextLoss = addMilli(contextLoss, 60);
      }
    }
  }
  contextLoss = clampMilli(contextLoss, 0, 1000);

  // --- Claim drift ---
  // Token.claimIds: unambiguous single-token claims only (e.g. ABANDONED).
  // Multi-token phrases activate only via Claim.implyingTokenGroups.
  let claimDrift: Milli = 0;
  const impliedClaims = new Set<string>();

  for (const caption of resolved.captions) {
    for (const tokenId of caption.tokenIds) {
      const token = tokenById.get(tokenId);
      if (!token) continue;
      for (const claimId of token.claimIds) {
        impliedClaims.add(claimId);
      }
    }
    for (const claim of pack.claims) {
      if (captionImpliesClaim(caption.tokenIds, claim)) {
        impliedClaims.add(claim.id);
      }
    }
  }

  for (const claimId of impliedClaims) {
    const claim = pack.claims.find((c) => c.id === claimId);
    if (!claim) continue;

    const supported = claim.supportingBeatIds.some((id) => retainedSet.has(id));
    const contradicted = claim.contradictingBeatIds.some((id) => retainedSet.has(id));

    if (!supported || contradicted) {
      claimDrift = addMilli(claimDrift, claim.driftWeightMilli);
      if (contradicted) {
        claimDrift = addMilli(claimDrift, mulMilli(claim.driftWeightMilli, 400));
      }
    }
  }
  claimDrift = clampMilli(claimDrift, 0, 1000);

  // --- Reach components ---
  const opening = retainedBeats[0] ? beatById.get(retainedBeats[0]) : undefined;
  const hookMilli = opening?.hookMilli ?? 100;

  const totalAuthoredTicks = pack.beats.reduce((n, b) => n + (b.endTick - b.startTick), 0);
  const retainedTicks = retainedBeats.reduce((n, id) => {
    const b = beatById.get(id);
    if (!b) return n;
    const cut = resolved.cuts.get(id);
    if (cut?.mode === "trim" && cut.inTick !== undefined && cut.outTick !== undefined) {
      return n + (cut.outTick - cut.inTick);
    }
    return n + (b.endTick - b.startTick);
  }, 0);
  const durationRatio =
    totalAuthoredTicks === 0 ? 0 : ((retainedTicks * 1000) / totalAuthoredTicks) | 0;
  const pacingMilli =
    durationRatio === 0 ? 0 : clampMilli(1000 - Math.abs(durationRatio - 350), 0, 1000);

  let intensityMilli: Milli = retainedBeats.reduce((n, id) => {
    const b = beatById.get(id);
    return addMilli(n, b?.intensityMilli ?? 0);
  }, 0);
  intensityMilli = clampMilli((intensityMilli / Math.max(1, retainedBeats.length)) | 0, 0, 1000);

  let presentationProduction: Milli = 0;
  for (const caption of resolved.captions) {
    for (const tokenId of caption.tokenIds) {
      const token = tokenById.get(tokenId);
      if (token) {
        intensityMilli = clampMilli(addMilli(intensityMilli, mulMilli(token.intensityMilli, 500)));
      }
    }
    const boost = captionPresentationScoreBoost(caption);
    intensityMilli = clampMilli(addMilli(intensityMilli, boost.intensityMilli));
    presentationProduction = addMilli(presentationProduction, boost.productionMilli);
  }

  const creativeBoost = creativePresentationScoreBoost(pack, resolved);
  intensityMilli = clampMilli(addMilli(intensityMilli, creativeBoost.intensityMilli));
  presentationProduction = addMilli(presentationProduction, creativeBoost.productionMilli);
  claimDrift = clampMilli(addMilli(claimDrift, creativeBoost.claimDriftMilli), 0, 1000);

  let audioSyncMilli: Milli = 200;
  let ominousBoost = 0;
  const sound = effectiveSound(resolved);
  if (sound && isAudibleSound(sound)) {
    const asset = assetById.get(sound.assetId);
    if (asset) {
      if (asset.tags.includes("ominous") || asset.tags.includes("alarm")) {
        ominousBoost = addMilli(ominousBoost, 180);
        intensityMilli = clampMilli(addMilli(intensityMilli, 150));
        if (omittedCritical.length > 0) {
          claimDrift = clampMilli(addMilli(claimDrift, 120));
        }
      }
      if (asset.tags.includes("warm")) {
        audioSyncMilli = addMilli(audioSyncMilli, 80);
      }
      audioSyncMilli = addMilli(audioSyncMilli, mulMilli(sound.volumeMilli, 200));
    }
  }
  audioSyncMilli = clampMilli(
    addMilli(audioSyncMilli, ominousBoost, creativeBoost.audioSyncMilli),
    0,
    1000,
  );

  const audienceFitMilli = clampMilli(
    1000 - Math.abs(intensityMilli - pack.audience.preferredIntensityMilli),
    0,
    1000,
  );

  const noveltyMilli = clampMilli(
    mulMilli(pack.audience.noveltyWeightMilli, addMilli(rng(), contextLoss / 2)),
    0,
    1000,
  );

  const productionMilli = clampMilli(
    addMilli(
      resolved.captions.length > 0 ? 200 : 50,
      sound && isAudibleSound(sound) ? 200 : 50,
      retainedBeats.length > 0 ? 200 : 0,
      150,
      presentationProduction,
    ),
    0,
    1000,
  );

  // Declared reach weights on retained beat tags.
  let tagReachMilli: Milli = 0;
  const retainedTagIds = new Set<string>();
  for (const beatId of retainedBeats) {
    const beat = beatById.get(beatId);
    if (!beat) continue;
    for (const tagId of beat.scoringTagIds) {
      retainedTagIds.add(tagId);
      const tag = tagById.get(tagId);
      if (tag) tagReachMilli = addMilli(tagReachMilli, tag.reachMilli);
    }
  }

  // Trends scale with fraction of matching tags present in retained material.
  let trendBoost: Milli = 0;
  for (const trend of pack.trends) {
    const present = trend.matchingTagIds.filter((tagId) => retainedTagIds.has(tagId)).length;
    if (present === 0) continue;
    const fraction = ((present * 1000) / trend.matchingTagIds.length) | 0;
    trendBoost = addMilli(trendBoost, mulMilli(trend.reachBoostMilli, fraction));
  }

  const { castPresentationReach, provenanceRisk } = resolveCastScoring(pack, resolved);

  const reach = clampMilli(
    addMilli(
      mulMilli(hookMilli, 220),
      mulMilli(pacingMilli, 140),
      mulMilli(intensityMilli, 220),
      mulMilli(audioSyncMilli, 120),
      mulMilli(audienceFitMilli, 120),
      mulMilli(noveltyMilli, 80),
      mulMilli(productionMilli, 100),
      tagReachMilli,
      trendBoost,
      mulMilli(contextLoss, 60),
      mulMilli(claimDrift, 40),
      castPresentationReach,
      creativeBoost.presentationReachMilli,
    ),
    0,
    10000,
  );

  const penalty = mulMilli(
    addMilli(
      mulMilli(contextLoss, 600),
      mulMilli(claimDrift, 800),
      // Tunable weight: provenanceRisk → delayed Cred penalty (0 when no/source-faithful cast).
      mulMilli(provenanceRisk, PROVENANCE_CRED_PENALTY_WEIGHT_MILLI),
    ),
    pack.audience.credibilitySensitivityMilli,
  );
  const credDelta = clampMilli(200 - penalty, -1000, 1000);

  return {
    reach,
    contextLoss,
    claimDrift,
    credDelta,
    provenanceRisk,
    components: {
      hookMilli,
      pacingMilli,
      intensityMilli,
      audioSyncMilli,
      audienceFitMilli,
      noveltyMilli,
      productionMilli,
    },
  };
}

/**
 * Shown reconstruction disclosure retains 40% of base provenance risk (material reduction,
 * not erasure). Hidden disclosure applies the full authored base risk.
 */
const DISCLOSURE_RISK_RETAIN_MILLI = 400;

/**
 * How strongly bounded provenanceRisk contributes to the delayed Cred penalty.
 * Same units as the contextLoss (600) / claimDrift (800) weights inside the credibility sum.
 * Zero provenanceRisk (no cast / source-faithful) leaves credDelta unchanged bit-for-bit.
 */
export const PROVENANCE_CRED_PENALTY_WEIGHT_MILLI = 700;

function resolveCastScoring(
  pack: SourcePack,
  resolved: ResolvedEdit,
): { castPresentationReach: Milli; provenanceRisk: Milli } {
  let castPresentationReach: Milli = 0;
  let provenanceRisk: Milli = 0;
  const slots = pack.castSlots ?? [];
  if (slots.length === 0 || resolved.casts.size === 0) {
    return { castPresentationReach, provenanceRisk };
  }
  const slotById = new Map(slots.map((s) => [s.id, s]));

  for (const [slotId, castOp] of resolved.casts) {
    const slot = slotById.get(slotId);
    if (!slot) continue;
    const variant = slot.variants.find((v) => v.id === castOp.variantId);
    if (!variant) continue;
    if (variant.sourceFaithful || variant.id === slot.defaultVariantId) {
      continue;
    }
    castPresentationReach = addMilli(castPresentationReach, variant.presentationReachMilli);
    let risk = variant.provenanceRiskMilli;
    if (castOp.showReconstructionDisclosure) {
      risk = mulMilli(risk, DISCLOSURE_RISK_RETAIN_MILLI);
    }
    provenanceRisk = addMilli(provenanceRisk, risk);
  }

  return {
    castPresentationReach,
    provenanceRisk: clampMilli(provenanceRisk, 0, 1000),
  };
}

function captionImpliesClaim(tokenIds: string[], claim: Claim): boolean {
  if (claim.implyingTokenGroups.length === 0) return false;
  const set = new Set(tokenIds);
  return claim.implyingTokenGroups.some((group) => group.every((id) => set.has(id)));
}
